import { atom } from 'jotai'
import { atomFamily, atomWithStorage } from 'jotai/utils'

import {
  DEFAULT_DISPLAY_SCALE,
  type DisplayScale,
  type ScaleSize,
  type ScaleUnit
} from '@/constants/display-scale'

export const serializeDisplayScale = (s: DisplayScale) =>
  `${s.unit}:${s.size}` as const

export const deserializeDisplayScale = (
  raw?: string | null
): DisplayScale | null => {
  if (!raw) return null
  const [unit, size] = raw.split(':') as [ScaleUnit, ScaleSize]
  const validUnit = unit === 'hour' || unit === 'day'
  const validSize = size === 'large' || size === 'medium' || size === 'small'
  if (validUnit && validSize) return { unit, size }
  return null
}

// 永続化キー: ユーザーごと
export const getDisplayScaleStorageKey = (userId: number) =>
  `display-scale:${String(userId)}`

type Params = { userId: number }

/**
 * 表示スケールAtom（ユーザー単位）
 * - 初期値は DEFAULT_DISPLAY_SCALE（= { unit: 'hour', size: 'large' }）
 * - 保存形式は "unit:size"（例: "hour:large"）
 */
export const displayScaleAtomFamily = atomFamily(
  (params: Params) => {
    const storageKey = getDisplayScaleStorageKey(params.userId)

    // "hour:medium" のような文字列で保存
    const base = atomWithStorage<string>(
      storageKey,
      serializeDisplayScale(DEFAULT_DISPLAY_SCALE),
      undefined,
      { getOnInit: true }
    )

    // 取得時は DisplayScale にデシリアライズ
    return atom(
      (get) => deserializeDisplayScale(get(base)) ?? DEFAULT_DISPLAY_SCALE,
      (_get, set, next: DisplayScale) => set(base, serializeDisplayScale(next))
    )
  },
  (a, b) => String(a.userId) === String(b.userId)
)
